/* eslint-disable no-console */
import { Inject, Injectable } from '@angular/core';
import { ApisDocuments, ApisRequiredFields } from '@src/app/core/constants/ib-checkin-apis-constants';
import { ApisPassenger } from '@src/app/core/models/ib-checkin-apis/ib-checkin-apis.model';
import { CountryCode } from '@src/app/core/models/ib-checkin-apis/services/additional-info-model';
import { Address, InfantPassengerSecurityInfo, MandatoryDocument, SecurityDocument, Type, Document } from '@src/app/core/models/ib-checkin-apis/services/security-model';
import { CheckinAppConstants } from '@src/components/routing/ib-check-in-constants';
import { IbUtilsService } from '@src/components/services/Utils/ib-utils.service';
import { ModelApisForm, ModelApisInfant, DocumentObject, ItemList } from 'commercial-management--digital--web-components-design--library/dist/components/layout/ApisForm/ApisForm.model';
import { ModelContactData } from 'commercial-management--digital--web-components-design--library/dist/components/layout/ApisForm/components/ContactData';
import { ModelAdditionalInfo } from 'commercial-management--digital--web-components-design--library/dist/components/layout/ApisForm/components/ContactData/AdditionalInfo/AdditionalInfo.model';
import { ModelEmergencyInfo } from 'commercial-management--digital--web-components-design--library/dist/components/layout/ApisForm/components/ContactData/EmergencyInfo/EmergencyInfo.model';
import { ModelPassengerAddress } from 'commercial-management--digital--web-components-design--library/dist/components/layout/ApisForm/components/PassengerAddress/PassengerAddress.model';
import { ModelPersonalData } from 'commercial-management--digital--web-components-design--library/dist/components/layout/ApisForm/components/PersonalData/PersonalData.model';
import { AJSRootScope, CommonsSessionStorageService } from 'ib-web-commons/utils';

@Injectable({ providedIn: 'root' })
export class IbCheckinApisModelTransformerService {
    constructor (@Inject(AJSRootScope) private $rootScope, private IbUtilsService: IbUtilsService, private CommonsSessionStorageService: CommonsSessionStorageService) {}

    setApisFormModel = (passenger: ApisPassenger): ModelApisForm => {
        const bookingRequiredInfo = this.getBookingRequiredInfo(passenger.id as string);
        const isRequiredContactData = bookingRequiredInfo.includes(ApisRequiredFields.CONTACT_DATA);
        const isRequiredEmergencyInfo = bookingRequiredInfo.includes(ApisRequiredFields.EMERGENCY_DATA);
        const visaOption = passenger.optionalDocuments.find((document) => document.type.code === ApisDocuments.VISA);
        const infant = passenger.infantPassengerSecurityInformation;
        const gdpr: any = this.CommonsSessionStorageService.get('gdprInfoIBPRIVAC');
        const model: ModelApisForm = {
            typePassenger: this.IbUtilsService.getPassengerType(passenger.id as string),
            personalData: this.fillPersonalData(passenger),
            passengerAddress: this.fillPassengerAddress(passenger),
            documentationRequired: this.fillDocumentationRequired(passenger),
            contactData: this.fillContactData(isRequiredContactData, isRequiredEmergencyInfo, passenger),
            infant: this.fillInfant(infant),
            id: Number(passenger.id),
            isPrivacyStatement: (Array.isArray(bookingRequiredInfo) && bookingRequiredInfo.includes(ApisRequiredFields.PRIVACY_STATEMENT)) || false,
            gdpr: {
                text: gdpr?.text ?? '',
                url: '' // TODO
            },
            bookingInfoPassenger: passenger.passengerBookingInfo
        };
        this.fillVisaOption(model, visaOption);
        return model;
    };

    fillContactData = (isRequiredContactData: boolean, isRequiredEmergencyInfo: boolean, passenger: ApisPassenger): ModelContactData => {
        return {
            additionalInfo: this.fillAdditionalInfo(isRequiredContactData, passenger),
            emergencyInfo: this.fillEmergencyInfo(isRequiredEmergencyInfo, passenger)
        };
    };

    fillEmergencyInfo = (isRequiredEmergencyInfo: boolean, { emergencyContact }: ApisPassenger): ModelEmergencyInfo => {
        const { phone, name } = emergencyContact ?? {};
        return {
            phone: {
                value: (phone?.countryCode as CountryCode)?.code ?? '',
                required: isRequiredEmergencyInfo
            },
            number: {
                value: phone?.number ?? '',
                required: isRequiredEmergencyInfo
            },
            name: {
                value: name ?? '',
                required: isRequiredEmergencyInfo
            },
            checkEmergencyAll: false
        };
    };

    fillAdditionalInfo = (isRequiredContactData: boolean, { additionalInfo }: ApisPassenger): ModelAdditionalInfo => {
        const { disruptionContact } = additionalInfo ?? {};
        return {
            email: {
                value: disruptionContact?.email ?? '',
                required: isRequiredContactData
            },
            phone: {
                value: (disruptionContact?.phone?.countryCode as CountryCode)?.code ?? '',
                required: isRequiredContactData
            },
            number: {
                value: disruptionContact?.phone?.number ?? '',
                required: isRequiredContactData
            },
            checkInfoAll: false
        };
    };

    fillInfant = (infant: InfantPassengerSecurityInfo): ModelApisInfant => {
        if (!infant) return undefined;
        return {
            typePassenger: 'INFANT',
            personalData: this.fillPersonalData(infant),
            documentationRequired: this.fillDocumentationRequired(infant)
        };
    };

    fillVisaOption = (model, visaOption) => {
        if (visaOption)
            model.documentationRequired.other.push({
                type: {
                    code: visaOption.type.code,
                    description: visaOption.type.description
                },
                country: visaOption.issuerCountry ? {
                    value: visaOption.issuerCountry,
                    label: ''
                } : undefined,
                expiryDate: visaOption.expiryDate ?? undefined,
                number: visaOption.number ?? undefined,
                requiredFields: visaOption.requiredFields,
                requiredFieldsOptions: visaOption.requiredFieldsOptions
            });
    };

    fillPersonalData = ({ requiredFields, name, surname, birthDate, nationality, homeAddress, gender }: ApisPassenger | InfantPassengerSecurityInfo): ModelPersonalData => {
        const { NAME, SURNAME, BIRTH_DATE, NATIONALITY, HOME_COUNTRY, GENDER } = ApisRequiredFields;

        const getNationalityString = (nationality: string | CountryCode | null): string => {
            if (typeof nationality === 'string') {
                return nationality;
            } else if (nationality?.code) {
                return nationality.code;
            }
        };

        const createField = (value: any, field: string, defaultValue: any = '') => {
            return {
                value: value ?? defaultValue,
                required: this.isRequiredField(requiredFields, field)
            };
        };

        const nationalityString = getNationalityString(nationality);

        return {
            name: createField(name, NAME),
            surname: createField(surname, SURNAME),
            birthDate: createField(birthDate, BIRTH_DATE),
            nationality: createField(nationalityString, NATIONALITY, ''),
            country: createField((homeAddress?.country as CountryCode)?.code, HOME_COUNTRY),
            gender: createField(gender, GENDER)
        };
    };

    transformDestinationAddress = (requiredFields: string[], destinationAddress: Address): ModelPassengerAddress => {
        const { address = '', zipCode = '', city = '', state = '' } = destinationAddress ?? {};
        const { DESTINATION_ADDRESS, DESTINATION_ZIP, DESTINATION_CITY, DESTINATION_STATE } = ApisRequiredFields;
        return {
            address: {
                value: address,
                required: this.isRequiredField(requiredFields, DESTINATION_ADDRESS)
            },
            zipCode: {
                value: zipCode,
                required: this.isRequiredField(requiredFields, DESTINATION_ZIP)
            },
            city: {
                value: city,
                required: this.isRequiredField(requiredFields, DESTINATION_CITY)
            },
            state: {
                value: state,
                required: this.isRequiredField(requiredFields, DESTINATION_STATE)
            }
        };
    };

    fillPassengerAddress = ({ requiredFields, destinationAddress }: ApisPassenger): ModelPassengerAddress => {
        const isRequiredDestinationAddress = this.isRequiredField(requiredFields, ApisRequiredFields.DESTINATION_ADDRESS);
        return isRequiredDestinationAddress ? this.transformDestinationAddress(requiredFields, destinationAddress) : undefined;
    };

    fillDocumentationRequired = (passenger: ApisPassenger | InfantPassengerSecurityInfo) => {
        const getFilledDocument = (documents: Document[]): Document | undefined => {
            const docTypes = ['PSPT', 'DNI'];
            return documents.find((doc: Document) => { return docTypes.includes((doc.type as Type).code);});
        };
        const document = getFilledDocument(passenger.documents);
        return {
            mandatory: passenger.mandatoryDocuments ? this.getDocumentsMandatory(passenger.mandatoryDocuments, document) : [],
            other: passenger.optionalDocuments ? this.getDocuments(passenger.optionalDocuments) : []
        };
    };

    getBookingRequiredInfo = (passengerId: string) => {
        const bookingPassengers = (this.CommonsSessionStorageService.get(CheckinAppConstants.STORAGE_KEYS.BOOKING_INFO) as any).passengers;
        return bookingPassengers.find((passenger) => passenger.id.toString() === passengerId)?.requiredInformation ?? [];
    };

    getDocumentsMandatory = (data: MandatoryDocument[], document?: Document): DocumentObject[] => {
        let mandatoryDocuments: DocumentObject[] = [];
        data?.forEach((item: MandatoryDocument) => {
            mandatoryDocuments.push(...this.getDocuments(item.documents, document));
        });

        return mandatoryDocuments;
    };

    getDocuments = (documents: SecurityDocument[], document?: Document): DocumentObject[] =>
        documents?.map(({ type, issuerCountry, number, expiryDate, requiredFields, requiredFieldsOptions }) => {
            const doc: DocumentObject = {
                type: {
                    code: type?.code,
                    description: type?.description
                },
                country: issuerCountry ? {
                    value: issuerCountry,
                    label: ''
                } : undefined,
                requiredFields,
                requiredFieldsOptions
            };
            if (document && (document.type as Type).code === (doc.type as ItemList).code) {
                doc.number = document.number ?? number;
                doc.expiryDate = document.expiryDate ?? expiryDate;
                if (document.issuerCountry) {
                    doc.country = {
                        value: (document.issuerCountry as Type).code ?? '',
                        label: (document.issuerCountry as Type).description ?? ''
                    };
                }
            }
            return doc;
        });

    isRequiredField = (arrFields: string[] | undefined, field: string): boolean => arrFields?.includes(field) ?? false;
}
